import { useState } from 'react';
import { Share2, Copy, Facebook, Twitter, MessageCircle, X, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function ShareMenu({ title, url, label = 'Compartir', compact = false, testid = 'share-menu' }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const fullUrl = url?.startsWith('http') ? url : `${window.location.origin}${url || window.location.pathname}`;
  const text = title ? `${title} — ` : '';
  const encodedText = encodeURIComponent(text + fullUrl);

  const links = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedText}`,
      color: 'bg-emerald-500 hover:bg-emerald-600',
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
      color: 'bg-[#1877F2] hover:bg-[#166fe0]',
    },
    {
      key: 'twitter',
      label: 'X / Twitter',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodedText}`,
      color: 'bg-slate-900 hover:bg-slate-800',
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const tryNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || 'CNIB Connect', url: fullUrl });
        return true;
      } catch {
        // Fallback to menu
      }
    }
    return false;
  };

  const handleOpen = async (e) => {
    e.stopPropagation();
    const used = await tryNative();
    if (!used) setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        data-testid={testid}
        className={
          compact
            ? 'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900'
            : 'flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200'
        }
      >
        <Share2 className="h-4 w-4" />
        {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-900/60 p-4 sm:items-center"
          onClick={() => setOpen(false)}
          data-testid={`${testid}-sheet`}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 p-5">
              <h3 className="font-heading text-lg font-extrabold text-slate-900">Compartir</h3>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900" data-testid={`${testid}-close`}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 p-5">
              {links.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${testid}-${l.key}`}
                  className="flex flex-col items-center gap-2 rounded-2xl p-3 transition-transform hover:-translate-y-0.5"
                  onClick={() => setOpen(false)}
                >
                  <span className={`flex h-12 w-12 items-center justify-center rounded-full text-white ${l.color}`}>
                    <l.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-bold text-slate-700">{l.label}</span>
                </a>
              ))}
            </div>
            <div className="border-t border-slate-100 p-5">
              <button
                type="button"
                onClick={copy}
                data-testid={`${testid}-copy`}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100"
              >
                <span className="truncate pr-3">{fullUrl}</span>
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
